import { useNavigate, type NavigateFunction, useLocation } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import routes from "./config";
import PageLoader from "../components/PageLoader";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const location = useLocation();
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(true);
  // The first view is the prerendered page: show it as it is. The hide/loader/scroll
  // transition below is for in-app navigations only.
  const isFirstView = useRef(true);

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  useEffect(() => {
    if (isFirstView.current) {
      isFirstView.current = false;
      return;
    }
    // Immediately hide content and show loader
    setShowContent(false);
    setIsLoading(true);
    
    // Disable smooth scrolling temporarily
    const html = document.documentElement;
    const originalScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    
    // Prevent body scroll during transition
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    // Use requestAnimationFrame to ensure scroll happens after content is hidden
    requestAnimationFrame(() => {
      // Scroll to top instantly while content is hidden
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      
      // Force scroll position
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Small delay to ensure scroll completes, then show new content
      setTimeout(() => {
        setShowContent(true);
        // Hide loader after content is shown
        setTimeout(() => {
          setIsLoading(false);
          document.body.style.overflow = originalOverflow;
          html.style.scrollBehavior = originalScrollBehavior;
        }, 200);
      }, 100);
    });
    
    return () => {
      document.body.style.overflow = originalOverflow;
      html.style.scrollBehavior = originalScrollBehavior;
    };
  }, [location.pathname]);

  return (
    <>
      {isLoading && <PageLoader />}
      <div style={{ 
        display: showContent ? 'block' : 'none',
        opacity: isLoading ? 0 : 1, 
        transition: 'opacity 0.2s ease-in-out'
      }}>
        {element}
      </div>
    </>
  );
}

